import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../../shared/services/api.service';
import { ToastService } from '../../../shared/services/toast.service';
import { AuthService } from '../../../shared/services/auth.service';

interface SecurityQuestion {
  id: number;
  question: string;
}

interface SelectedQuestion {
  questionId: number;
  question: string;
  answer: string;
}

@Component({
  selector: 'app-security-questions',
  templateUrl: './security-questions.component.html',
  standalone: false,
  styleUrls: ['./security-questions.component.scss']
})
export class SecurityQuestionsComponent implements OnInit {
  availableQuestions: SecurityQuestion[] = [];
  selectedQuestions: SelectedQuestion[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  hasSecurityQuestions: boolean = false;

  constructor(
    private api: ApiService,
    private toast: ToastService,
    private auth: AuthService
  ) {}

  ngOnInit() {
    this.loadAvailableQuestions();
  }

  loadAvailableQuestions() {
    this.api.getData('/partners/noAuth/security-questions').subscribe((res: any) => {
      if (res && !res.error) {
        this.availableQuestions = res.data || [];
      }
    });
  }

  addQuestion() {
    if (this.selectedQuestions.length >= 3) {
      this.toast.show('Maximum 3 questions allowed', 'warning');
      return;
    }
    this.selectedQuestions.push({
      questionId: -1,
      question: '',
      answer: ''
    });
  }

  removeQuestion(index: number) {
    this.selectedQuestions.splice(index, 1);
  }

  onQuestionChange(index: number) {
    const selected = this.selectedQuestions[index];
    if (selected.questionId >= 0) {
      const q = this.availableQuestions.find(q => q.id === selected.questionId);
      if (q) {
        selected.question = q.question;
      }
    }
  }

  validateQuestions(): string[] {
    const errors: string[] = [];
    if (this.selectedQuestions.length < 2) {
      errors.push('Select at least 2 security questions');
    }
    
    const questionIds = this.selectedQuestions.map(q => q.questionId);
    if (new Set(questionIds).size !== questionIds.length) {
      errors.push('Duplicate questions not allowed');
    }
    
    for (let i = 0; i < this.selectedQuestions.length; i++) {
      const q = this.selectedQuestions[i];
      if (q.questionId < 0 || !q.question) {
        errors.push(`Question ${i + 1}: Please select a question`);
      }
      if (!q.answer || q.answer.trim().length < 2) {
        errors.push(`Question ${i + 1}: Answer must be at least 2 characters`);
      }
    }
    
    return errors;
  }

  saveSecurityQuestions() {
    const errors = this.validateQuestions();
    if (errors.length > 0) {
      this.errorMessage = errors.join(', ');
      return;
    }

    this.errorMessage = '';
    this.isLoading = true;

    const user = this.auth.getUser();
    const email = user?.email || '';

    const questionsPayload = this.selectedQuestions
      .filter(q => q.questionId >= 0 && q.answer)
      .map(q => ({
        questionId: q.questionId,
        answer: q.answer.trim()
      }));

    this.api.postData('/partners/noAuth/security-questions/set', {
      email: email,
      questions: questionsPayload
    }).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res && !res.error) {
          this.successMessage = 'Security questions saved successfully!';
          this.toast.show(this.successMessage, 'success');
          this.hasSecurityQuestions = true;
          this.selectedQuestions = [];
        } else {
          this.errorMessage = res?.message || 'Failed to save security questions';
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = (typeof err === 'string' ? err : err?.message) || 'Failed to save security questions';
      }
    });
  }
}
